import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  "From: I need more followers → To: A small engaged audience is enough",
  "From: AI content feels fake → To: AI amplifies human strategy",
  "From: More posts = more growth → To: Right posts = right buyers",
];

export default function BeliefJourney() {
  const [gap, setGap] = useState("");
  const [journeys, setJourneys] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/belief/list");
    setJourneys(data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!gap.trim()) { toast.error("Describe the belief gap first"); return; }
    setBusy(true);
    toast.message("Designing the 7-step belief journey…");
    try {
      await runTask("/api/belief/generate", { belief_gap: gap }, { intervalMs: 2500, maxMs: 180000 });
      setGap("");
      await load();
      toast.success("Journey ready");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Nurture Architect · Belief Journeys</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Design the 7-step journey to a buying decision.</h1>
        </div>
      </div>

      <div className="p-8 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-5 border border-border rounded p-6 bg-card" data-testid="belief-form">
          <Label className="text-xs uppercase tracking-widest">Belief gap to close</Label>
          <div className="mt-1 text-xs text-muted-foreground">Format: <span className="font-mono">From: X → To: Y</span></div>
          <Input
            data-testid="input-belief-gap"
            value={gap}
            onChange={(e) => setGap(e.target.value)}
            placeholder="From: X → To: Y"
            className="mt-3 h-11"
          />
          <div className="mt-4 space-y-2">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Try one</div>
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => setGap(ex)}
                data-testid={`belief-example-${i}`}
                className="w-full text-left text-sm border border-border rounded p-3 hover:bg-secondary transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
          <Button
            data-testid="btn-generate-belief"
            className="mt-6 w-full h-11"
            onClick={generate}
            disabled={busy}
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Designing…</> : <><Sparkles className="h-4 w-4"/> Generate 7-step journey</>}
          </Button>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-4" data-testid="belief-list">
          {journeys.length === 0 && (
            <div className="border border-dashed border-border rounded p-8 text-sm text-muted-foreground">
              No journeys yet. Describe a belief gap and generate your first sequence.
            </div>
          )}
          {journeys.map((j) => (
            <div key={j.id} className="border border-border rounded p-6 bg-card" data-testid={`journey-${j.id}`}>
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Belief gap</div>
              <div className="mt-1 font-display font-bold text-lg leading-tight">{j.belief_gap}</div>
              <div className="text-sm text-muted-foreground mt-1">Target: {j.target_belief}</div>

              <div className="mt-5 space-y-2">
                {(j.steps || []).map((s, i) => (
                  <div key={i} className="flex gap-3 border border-border rounded p-3 bg-background">
                    <div className="font-display font-black text-2xl leading-none w-10 text-primary shrink-0">{String(s.step).padStart(2, "0")}</div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold text-sm">{s.title}</div>
                        <span className="pillar-badge">{s.format}</span>
                      </div>
                      <div className="mt-1 text-sm">“{s.hook}”</div>
                      <div className="mt-1 text-xs text-muted-foreground">{s.intent}</div>
                    </div>
                    {i < (j.steps.length - 1) && <ArrowRight className="h-4 w-4 text-muted-foreground self-center shrink-0"/>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
